import { Component, OnDestroy, OnInit, ViewEncapsulation, Input, HostBinding } from '@angular/core';
import { of, Subscription, catchError } from 'rxjs';
import { FieldExpression, NodeExpression, toString as luceneToString } from 'lucene';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor } from '@angular/common';

import { CustomWidget, Widget } from '../../../../models/widget';
import { UserService } from '../../../../services/user.service';
import { User } from '../../../../models/user';
import { PaginatedResult } from '../../../../models/paginated-result';
import { Batch } from '../../../../../order/models/batch';
import { pageTransitions } from '../../../../animations/page-transitions';
import { BatchesService } from '../../../../../order/services/batches.service';
import { WEIGHT_OPTIONS, PRODUCT_OPTIONS } from '../../../../models/list';
import { OrderByDirection, OrderBy } from '../../../../utils/constant';
import { NumberPipe } from '../../../../pipes/number.pipe';
import { LoadingSpinnerComponent } from '../../../utils/loading-spinner.component';

interface ContentType {
	type: 'steelgrade' | 'customerArticleReference';
}

enum FieldContent {
	CUSTOMER_ARTICLE = 'customerArticleReference',
	STEELGRADE = 'steelgrade'
}

enum FieldWeight {
	GROSS = 'qtyBatchGross',
	NET = 'qtyBatchNet'
}

interface RtsData {
	value: number;
	reference: string;
}

const GROUP_STATUS_READY_TO_SHIP = '2';

@Component({
	selector: 'copo-biggest-stocks-widget',
	templateUrl: './biggest-stocks-widget.component.html',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,

	imports: [NgIf, TranslateModule, MatTooltipModule, NgFor, LoadingSpinnerComponent, NumberPipe]
})
export class BiggestStocksWidgetComponent implements OnInit, OnDestroy, CustomWidget {
	@HostBinding('class.widget-content') readonly widgetContent = true;
	@Input() widget!: Widget;
	rtsValues: RtsData[] = [];
	user: User;
	batchesSub?: Subscription;
	weightTypeLabel = '';
	productTypeLabel = '';
	loadingData = true;

	constructor(private readonly batchesService: BatchesService, private readonly userService: UserService) {
		this.user = this.userService.user;
	}

	ngOnInit() {
		const contentType = (<ContentType> this.widget.content).type;
		const weightTypeMapped = FieldWeight.GROSS;
		let contentFields;
		if (contentType === 'steelgrade') {
			contentFields = <const> [FieldContent.STEELGRADE, 'steelgradeDescription'];
			this.productTypeLabel = PRODUCT_OPTIONS.steelgrade;
		} else {
			contentFields = <const> [FieldContent.CUSTOMER_ARTICLE];
			this.productTypeLabel = PRODUCT_OPTIONS.article;
		}
		this.weightTypeLabel = WEIGHT_OPTIONS.gross;
		this.batchesSub = this.batchesService.getBatches(
			[...contentFields, weightTypeMapped],
			this.batchesFilter(),
			0,
			5,
			new OrderBy(weightTypeMapped, OrderByDirection.DESC),
			true
		).pipe(catchError((err: unknown) => {
			console.error('error retrieving orders', err);
			const emptyBatchArray: Batch[] = [];
			return of({ results: emptyBatchArray, count: 0, page: 1, limit: 1 });
		})).subscribe((batches: PaginatedResult<Batch>) => {
			this.loadingData = false;

			for (const batch of batches.results) {
				const ref = batch.steelgradeDescription || batch.customerArticleReference;
				this.rtsValues.push(
					{
						value: batch[weightTypeMapped],
						reference: ref
					}
				);
			}
		});
	}

	ngOnDestroy() {
		if (this.batchesSub) {
			this.batchesSub.unsubscribe();
		}
	}

	batchesFilter() {
		const allNode: FieldExpression | NodeExpression = {
			field: 'statusGroup',
			term: GROUP_STATUS_READY_TO_SHIP,
			quoted: true
		};

		return luceneToString(allNode);
	}
}
